<template>
	<section
		v-if="showSection"
		class="bg-gradient-to-b md:bg-gradient-to-t from-[#F3F7FD]"
		data-path="sectionsLocations"
	>
		<AdaptiveContainer class="flex justify-between items-center">
			<BaseHeading
				type="h5"
				class="my-9 col-span-full relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
			>
				{{ $t("locationJobs.otherLocations") }}
			</BaseHeading>
			<div class="text-sm hidden sm:flex">
				<NuxtLink :to="`/${$i18n.locale}/locations`" class="text-sm">
					{{ $t("locationJobs.allLocations") }}
					<span class="material-icons-outlined pl-2 text-base">arrow_forward</span>
				</NuxtLink>
			</div>
		</AdaptiveContainer>

		<AdaptiveContainer>
			<div v-if="loading" class="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-8">
				<div
					v-for="index in numberOfLocationsToShow"
					:key="index"
					class="h-[168px] rounded-big border border-dj-gray-light bg-dj-gray-minimal animate-pulse"
					:class="{ 'hidden sm:block': index > 4 }"
				></div>
			</div>

			<div v-else class="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-8">
				<CardLocationJobs
					v-for="(item, index) in locationCards"
					:key="item.id"
					:class="{ 'hidden sm:block': index >= 4 }"
					:href="item.href"
					:name="item.name"
					:jobs-count="item.jobsCount"
					:categories="item.categories"
				/>
			</div>
		</AdaptiveContainer>

		<div class="sm:hidden mb-6">
			<NuxtLink
				:to="`/${$i18n.locale}/locations`"
				class="w-full flex justify-center items-center bg-dj-blue/5 text-sm text-dj-blue hover:bg-dj-blue/10 active:bg-dj-blue/10 max-w-[320px] mx-auto mb-4 p-3 rounded-normal"
			>
				{{ $t("locationJobs.allLocations") }}
				<span class="material-icons-outlined pl-2">arrow_forward</span>
			</NuxtLink>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { constructApiUrl } from "~/composables/helpers";
import { mapLocationsToCards, type LocationCard, type LocationCardSource } from "~/composables/locationCards";

type PrioritizedLocation = Pick<LocationCardSource, "id" | "name" | "slug" | "jobs_count" | "categories">;

const { locale } = useI18n();
const regionCode = useRegion.getCurrentRegion().code.toLowerCase();
const numberOfLocationsToShow = 8;

const { data: fetchedLocations, pending: loading } = await useAsyncData(
	`home-locations-grid-${locale.value}-${regionCode}-${globalConfig.isBlue ? "blue" : "default"}-${
		globalConfig.isNotBlueHU ? "not-blue" : "all"
	}`,
	async () => {
		const url = constructApiUrl("/tags/prioritized/locations");
		url.searchParams.append("region", regionCode);

		const response = await $fetch<{ data: PrioritizedLocation[] }>(url.href);
		return response?.data || [];
	},
	{ default: () => [] }
);

const locationCards = computed<LocationCard[]>(() => {
	return mapLocationsToCards(fetchedLocations.value || [], locale.value, numberOfLocationsToShow);
});

const showSection = computed(() => loading.value || locationCards.value.length > 0);
</script>
